import Parameter from "./parameter";
import ModuleCard from "./module-card";
import Msg from "./message";
import Format from "./format";
import FieldPromote from "./field-promote";
import Field from "./field";
import Entity from "./entity";
import Documents from "./documents";
import DocumentValue from "./document-value";
import { clone, sortBy } from "../utils";
import IPlanningFilter from "../interface/planning-filter";
import IPlanningEvent from "../interface/planning-event";
import IPlanningDataset from "../interface/planning-dataset";
import IPlanningData from "../interface/planning-data";
import IPlanningColorValue from "../interface/planning-color-value";
import IPlanningColor from "../interface/planning-color";
import IPlanning from "../interface/planning";
import IModuleCard from "../interface/module-card";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import ICardPlanning from "../interface/card-planning";
import Global from "../global";
import ParamStorage from "../enum/param-storage";
import Message from "../enum/message";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import DateType from "../enum/date-type";
import CardType from "../enum/card-type";
import DefaultConfig from "../default-config";
import ParameterStorage from "../class/parameter-storage";

class PlanningSingleton {

    public getAll(): IPlanning[] {
        return this.getStore().getAll();
    }

    public get(id: number): IPlanning | null {
        return this.getStore().get(id);
    }

    public async create(planning: IPlanning): Promise<IPlanning> {
        if (!planning.colors) {
            planning.colors = [];
        }
        await this.processFields(planning);
        return await this.getStore().create(planning);
    }

    public async update(planning: IPlanning): Promise<IPlanning> {
        const original: IPlanning | null = this.get(planning.id as number);
        if (original) {
            await this.processFields(planning);
            const colorFieldsIds: number[] = [];
            planning.datasets.forEach((dataset: IPlanningDataset) => {
                if (dataset.colorFieldId) {
                    colorFieldsIds.push(dataset.colorFieldId);
                }
            });
            planning.colors = original.colors.filter((color: IPlanningColor) => {
                return colorFieldsIds.includes(color.fieldId);
            });
            return await this.getStore().update(planning);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async delete(id: number, deleteCard: boolean = true): Promise<number> {
        if (deleteCard) {
            const cards: IModuleCard[] = ModuleCard.getAll();
            for (let i = 0; i < cards.length; i++) {
                const card: IModuleCard = cards[i];
                if (card.type === CardType.Planning && (card as ICardPlanning).planningId === id) {
                    await ModuleCard.delete(card.id as number);
                }
            }
        }
        return this.getStore().delete(id);
    }

    public async rename(id: number, name: string): Promise<IPlanning | null> {
        const planning: IPlanning | null = this.getStore().get(id);
        if (planning) {
            planning.name = name;
            await this.getStore().update(planning);
            return this.getStore().get(id);
        }
        return null;
    }

    public async clone(id: number): Promise<IPlanning> {
        const origin: IPlanning | null = this.get(id);
        if (origin) {
            const planning: IPlanning = clone(origin);
            delete planning.id;
            return await this.getStore().create(planning);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async getData(id: number): Promise<IPlanningData> {
        const events: IPlanningEvent[] = [];
        const filters: IPlanningFilter[] = [];
        const colors: IPlanningColorValue[] = [];
        const planning: IPlanning | null = this.get(id);
        if (planning) {
            for (let i = 0; i < planning.datasets.length; i++) {
                const dataset: IPlanningDataset = planning.datasets[i];
                const entity: IEntity | null = Entity.get(dataset.entityId);
                if (entity) {
                    const documents: IDocument[] = await Documents.loadDocuments(dataset.entityId);
                    await Documents.loadRelatedDocs(entity, documents);
                    Documents.setLinkedFieldValues(entity, documents);
                    for (let j = 0; j < documents.length; j++) {
                        const doc: IDocument = documents[j];
                        const label: string[] = [];
                        const event: IPlanningEvent = {
                            id: '',
                            entityId: dataset.entityId,
                            recordId: doc.id as number,
                            label: '',
                            from: doc[`c${dataset.fromFieldId}`] as string || '',
                            to: '',
                            data: doc,
                            filterId: null,
                            colorId: null
                        };
                        if (dataset.toFieldId) {
                            event.to = doc[`c${dataset.toFieldId}`] as string || ''
                        } else if (event.from && dataset.durationFieldId) {
                            const duration: number | null = DocumentValue.getNumber(doc[`c${dataset.durationFieldId}`]);
                            if (duration !== null) {
                                event.to = (new Date((new Date(event.from)).getTime() + duration * Global.MS_IN_MINUTE)).toISOString();
                            }
                        }
                        if (event.from && !event.to) {
                            const time: number = dataset.defaultDuration * Global.MS_IN_MINUTE;
                            event.to = (new Date((new Date(event.from)).getTime() + time)).toISOString();
                        }
                        /* Validation */
                        if (event.from && event.to) {
                            event.id = `${event.entityId},${event.recordId}`;
                            dataset.labelFieldsId.forEach((fieldId: number) => {
                                const field: IField | null = Field.get(fieldId);
                                if (field) {
                                    if (field.uiType === FieldType.linked) {
                                        label.push(doc[`d${field.id}`] as string);
                                    } else {
                                        label.push(Format.byFieldId(doc[`c${field.id}`], field.id));
                                    }
                                }
                            });
                            event.label = label.join(' ').trim();
                            if (!event.label) {
                                event.label = Msg.get(Message.emptyevent);
                            }
                            if (dataset.colorFieldId) {
                                const colorField: IField | null = Field.get(dataset.colorFieldId);
                                if (colorField) {
                                    const colorValue: IDocumentValue = this.getEventValue(doc, colorField);
                                    if (colorValue !== null) {
                                        event.colorId = `${dataset.colorFieldId},${colorValue}`;
                                        const item: IPlanningColorValue | null = colors.find((c: IPlanningColorValue) => c.id === event.colorId) || null;
                                        if (!item) {
                                            let name: string = Format.byFieldId(colorValue, dataset.colorFieldId);
                                            if (colorField.uiType === FieldType.linked) {
                                                name = event.data[`d${colorField.id}`] as string;
                                            }
                                            const color: IPlanningColor | null = planning.colors.find((c: IPlanningColor) => {
                                                return c.value === colorValue && c.fieldId === dataset.colorFieldId;
                                            }) || null;
                                            colors.push({
                                                id: event.colorId,
                                                fieldId: dataset.colorFieldId,
                                                color: color ? color.color : '',
                                                value: colorValue,
                                                name
                                            });
                                        }
                                    }
                                }
                            }
                            if (dataset.filterFieldId) {
                                const filterField: IField | null = Field.get(dataset.filterFieldId);
                                if (filterField) {
                                    const filterValue: IDocumentValue = this.getEventValue(doc, filterField);
                                    if (filterValue !== null) {
                                        event.filterId = `${dataset.filterFieldId},${filterValue}`;
                                        const filter: IPlanningFilter | null = filters.find((f: IPlanningFilter) => f.id === event.filterId) || null;
                                        if (!filter) {
                                            let name: string = Format.byFieldId(filterValue, dataset.filterFieldId);
                                            if (filterField.uiType === FieldType.linked) {
                                                name = event.data[`d${filterField.id}`] as string;
                                            }
                                            filters.push({
                                                id: event.filterId,
                                                name
                                            });
                                        }
                                    }
                                }
                            }
                            events.push(event);
                        }
                    }
                }
            }
            return {
                filters: sortBy(filters, 'id'),
                colors,
                events
            };
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async setColors(planningId: number, colors: IPlanningColor[]): Promise<IPlanning> {
        const planning: IPlanning | null = this.get(planningId);
        if (planning) {
            planning.colors = colors;
            return await this.getStore().update(planning);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Methods */

    private async processFields(planning: IPlanning): Promise<void> {
        for (let i = 0; i < planning.datasets.length; i++) {
            if (planning.datasets[i].fromFieldId === -1) {
                const field: IField = await Field.create(planning.datasets[i].entityId, Msg.get(Message.fieldfrom));
                await FieldPromote.toDatetime(field.id, DefaultConfig[FieldType.datetime](DateType.DateTime));
                planning.datasets[i].fromFieldId = field.id;
            }
            if (planning.datasets[i].toFieldId === -1) {
                const field: IField = await Field.create(planning.datasets[i].entityId, Msg.get(Message.fieldto));
                await FieldPromote.toDatetime(field.id, DefaultConfig[FieldType.datetime](DateType.DateTime));
                planning.datasets[i].toFieldId = field.id;
            }
            if (planning.datasets[i].durationFieldId === -1) {
                const field: IField = await Field.create(planning.datasets[i].entityId, Msg.get(Message.fieldduration));
                await FieldPromote.toNumber(field.id, DefaultConfig.PlanningDurationField());
                planning.datasets[i].durationFieldId = field.id;
            }
        }
    }

    private getEventValue(doc: IDocument, field: IField): IDocumentValue {
        let fieldId: number = field.id;
        if (field.uiType === FieldType.linked) {
            fieldId = field.linkedRelation[0];
        }
        let value: IDocumentValue = doc[`c${fieldId}`];
        if (value instanceof Array) {
            value = value[0];
        }
        if (value === undefined) {
            value = null;
        }
        return value;
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IPlanning> {
        return Parameter.get(ParamStorage.Plannings);
    }

}

const Planning: PlanningSingleton = new PlanningSingleton();

export default Planning;